import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest, formatDate } from '../../utils';
import './MemberHome.css';

const fallbackEvent = require('../../assets/images/fallback/event.png');

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Ecosystem member's "My Vouchers" screen (reached from the member home badge). Two buckets:
   the vouchers they can USE themselves (assigned / allow-listed — applied automatically at
   checkout, so this is informational + a shortcut to book) and the ones they can SHARE
   (distributable — a per-Lab "invite a friend with this voucher" link they hand out, either
   copied as a ready-to-send message or emailed for them, exactly as on My Network).
   Backend: GET /api/bravos/my-vouchers, POST /api/bravos/network/voucher-invite. */
const MyVouchers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  // The email form is opened per (voucher, Lab) row — the key of the row it belongs to.
  const [mailFor, setMailFor] = useState(null);
  const [mailForm, setMailForm] = useState({ first_name: '', last_name: '', email: '' });
  const [mailNotice, setMailNotice] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/my-vouchers')
      .then((res) => {
        if (res && res.redirect) { navigate(res.redirect); return; }
        setData(res);
      })
      .catch(() => navigate('/member-home'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Copy a warm, ready-to-send message + the link (not a bare URL), so it drops
  // straight into WhatsApp/email and the recipient sees why they got it.
  const copyLink = (key, link, message) => {
    const text = message ? `${message}\n${link}` : link;
    const done = () => { setCopied(key); setTimeout(() => setCopied(null), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };

  // The other way to hand the voucher over: instead of copying the message, we mail it for them.
  const openMailForm = (key) => {
    setMailNotice(null);
    setMailForm({ first_name: '', last_name: '', email: '' });
    setMailFor(mailFor === key ? null : key);
  };

  const sendVoucherEmail = (voucher, lab) => {
    if (sending) return;
    const first = mailForm.first_name.trim();
    const last = mailForm.last_name.trim();
    const email = mailForm.email.trim();
    if (!first || !last) { setMailNotice(t('fillAllFields')); return; }
    if (!EMAIL_PATTERN.test(email)) { setMailNotice(t('invalidEmail')); return; }
    setSending(true);
    setMailNotice(null);
    // `code` pins the send to the voucher of THIS row — this screen lists every distributable
    // voucher separately, so the API must not fall back to whichever one comes first.
    apiRequest('POST', '/bravos/network/voucher-invite', {
      event_id: lab.event_id, code: voucher.code, first_name: first, last_name: last, email,
    })
      .then(() => {
        setMailFor(null);
        setMailForm({ first_name: '', last_name: '', email: '' });
        setMailNotice(t('voucherInviteSent', { name: first }));
      })
      .catch((err) => setMailNotice(t(err.message, { defaultValue: t('reinviteFailed') })))
      .finally(() => setSending(false));
  };

  const held = (data && data.held) || [];
  const share = (data && data.share) || [];

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          <div>
            <Title my="0" className="mt-1">{t('myVouchersTitle')}</Title>
            <p className="bravos-brand__name">{t('myVouchersNote')}</p>
          </div>
          <button
            type="button"
            className="bravos-wallet__link bravos-members__back"
            onClick={() => navigate('/member-home')}
          >
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      {loading ? (
        <Card><span className="auth__spinner" /></Card>
      ) : (
        <>
          {held.length === 0 && share.length === 0 && (
            <Card><p className="bravos-q__hint">{t('noVouchersYet')}</p></Card>
          )}

          {held.length > 0 && (
            <Card>
              <Title my="1" className="mt-2">
                <Icon name="ticket" theme="primary" className="me-2" />
                {t('vouchersYouCanUse')}
              </Title>
              {held.map((v) => (
                <div key={v.code} className="bravos-member__alloc">
                  <span className="bravos-member__alloc-lab">
                    <span className="bravos-badge bravos-badge--voucher">🎟️ {v.offer}</span>
                    <span className="bravos-member__alloc-name">{t('voucherAutoAppliedHint')}</span>
                  </span>
                </div>
              ))}
              <button
                type="button"
                className="bravos-event__claim mt-2"
                onClick={() => navigate('/member-home')}
              >
                {t('bookALab')}
              </button>
            </Card>
          )}

          {share.length > 0 && (
            <Card>
              <Title my="1" className="mt-2">
                <Icon name="ticket" theme="primary" className="me-2" />
                {t('vouchersToShare')}
              </Title>
              {mailNotice && <p className="bravos-cal__notice">{mailNotice}</p>}
              {share.map((v, idx) => (
                <div key={v.code} style={{ marginTop: idx ? '14px' : 0 }}>
                  <p className="bravos-q__hint mb-1" style={{ color: '#1a7f4b' }}>
                    <Icon name="ticket" className="me-1" />
                    {v.remaining === null
                      ? t('vouchersUnlimited', { offer: v.offer })
                      : t('vouchersLeft', { offer: v.offer, count: v.remaining })}
                  </p>
                  {v.labs.map((l) => {
                    const key = `v-${v.code}-${l.event_id}`;
                    return (
                      <div key={l.event_id}>
                        <div className="bravos-member__alloc bravos-member__alloc--net">
                          <span className="bravos-member__alloc-lab">
                            <span
                              className="bravos-event__thumb"
                              style={{ backgroundImage: `url("${l.image_link || fallbackEvent}")` }}
                            />
                            <span className="bravos-member__alloc-name">
                              {l.event_name}
                              {l.date && <span className="bravos-member__alloc-type"> · {formatDate(l.date)}</span>}
                            </span>
                          </span>
                          {/* One under the other: both labels are long, and a wrapped pair beside
                              the Lab photo reads as a ragged cluster rather than two choices. */}
                          <span className="bravos-netlab__actions bravos-netlab__actions--stack">
                            {/* Either copy the ready-made message yourself… */}
                            <button
                              type="button"
                              className="bravos-member__reclaim bravos-netpick__voucher"
                              onClick={() => copyLink(key, l.voucher_link,
                                t('bravosVoucherInviteShareText', { event: l.event_name, code: l.voucher_code }))}
                            >
                              <Icon name="ticket" className="me-1" />
                              {copied === key ? t('linkCopied') : t('copyInviteLink')}
                            </button>
                            {/* …or let us email the same message for you. */}
                            <button
                              type="button"
                              className="bravos-member__reclaim bravos-netpick__voucher"
                              onClick={() => openMailForm(key)}
                            >
                              <Icon name="envelope" className="me-1" />
                              {t('emailMyVoucher')}
                            </button>
                          </span>
                        </div>

                        {mailFor === key && (
                          <div className="bravos-netform">
                            <p className="bravos-netform__hint">
                              {t('emailMyVoucherHint', { event: l.event_name })}
                            </p>
                            <input
                              className="bravos-form__input mb-2"
                              placeholder={t('firstName')}
                              value={mailForm.first_name}
                              maxLength={100}
                              onChange={(e) => setMailForm((f) => ({ ...f, first_name: e.target.value }))}
                            />
                            <input
                              className="bravos-form__input mb-2"
                              placeholder={t('lastName')}
                              value={mailForm.last_name}
                              maxLength={100}
                              onChange={(e) => setMailForm((f) => ({ ...f, last_name: e.target.value }))}
                            />
                            <input
                              className="bravos-form__input mb-2"
                              type="email"
                              placeholder={t('email')}
                              value={mailForm.email}
                              maxLength={120}
                              onChange={(e) => setMailForm((f) => ({ ...f, email: e.target.value }))}
                            />
                            <div className="bravos-netform__buttons">
                              <button
                                type="button"
                                className="bravos-member__reclaim"
                                onClick={() => setMailFor(null)}
                              >
                                {t('cancel')}
                              </button>
                              <button
                                type="button"
                                className="bravos-event__claim"
                                onClick={() => sendVoucherEmail(v, l)}
                                disabled={sending}
                              >
                                <Icon name="paper-plane" className="me-1" />
                                {sending ? t('pleaseWait') : t('send')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </Base>
  );
};

export default MyVouchers;
